// frontend/src/components/features/Login.jsx
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useGoogleLogin } from '@react-oauth/google';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { authService } from "../../services/auth";
import { analyticsService } from "../../services/analytics";
import { getCurrentLegalDocument } from "../../services/legal";
import { OAUTH_CONFIG } from "../../config/oauth";
import { API_BASE, FRONTEND_URL } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import GetTheAppCTA from "../GetTheAppCTA";
import VerificationPending from "../auth/VerificationPending";

function Login({ onLogin }) {
  const navigate = useNavigate();
  const oauthProcessingRef = useRef(false);
  const frontendHost = new URL(FRONTEND_URL).host;
  // Check for register query parameter
  const searchParams = new URLSearchParams(window.location.search);
  const shouldRegister = searchParams.get('register') === 'true';
  const pendingPersonaTitle = searchParams.get('persona_title') || '';
  const pendingPersonaIcon = searchParams.get('persona_icon') || '';
  const pendingInviteToken = searchParams.get('invite_token') || '';
  const [isRegister, setIsRegister] = useState(shouldRegister);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [currentLegalVersion, setCurrentLegalVersion] = useState(null);

  // Load the latest legal document version so registration can record consent.
  useEffect(() => {
    let cancelled = false;
    getCurrentLegalDocument()
      .then((doc) => {
        if (!cancelled) setCurrentLegalVersion(doc.version);
      })
      .catch(() => {
        // Fail silently; if the backend has no legal document the registration
        // will still complete and the post-login popup will prompt for consent.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Check if running on mobile (Capacitor native platform)
  const isMobile = Capacitor.isNativePlatform();

  // Shared post-auth handler: attempt to claim a pending invite then redirect.
  // Called by all three auth paths (email/password, Google OAuth, mobile OAuth).
  const handlePostAuth = React.useCallback(async () => {
    if (pendingInviteToken) {
      try {
        const claimRes = await httpRequest(`${API_BASE}/invites/session/${pendingInviteToken}/claim`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
        });
        if (claimRes.ok) {
          analyticsService.track('session_invite_claimed', { token: pendingInviteToken });
          navigate('/past-sessions');
          return;
        }
      } catch {
        // Non-fatal: claim failure should not block login
      }
    }
    navigate("/");
  }, [pendingInviteToken, navigate]);

  // Handle OAuth callback from redirect (mobile)
  React.useEffect(() => {
    const checkOAuthCallback = () => {
      const hash = window.location.hash;
      const url = window.location.href;

      // Check if we're on the redirect URI page (web app loaded in WebView) OR if we have OAuth token in hash
      // This handles both: WebView navigating to web app, and direct hash in current page
      const isRedirectPage = OAUTH_CONFIG.isRedirectPage(url);
      const hasOAuthToken = hash && hash.includes('access_token=');

      if ((isRedirectPage || isMobile) && hasOAuthToken) {
        if (oauthProcessingRef.current) return;

        const params = new URLSearchParams(hash.substring(1));
        const accessToken = params.get('access_token');
        const error = params.get('error');
        const errorDescription = params.get('error_description');

        if (error) {
          setError(`OAuth error: ${error}. ${errorDescription || ''}`);
          setOauthLoading(null);
          window.history.replaceState(null, '', window.location.pathname);
          localStorage.removeItem('oauth_redirect_pending');
          return;
        }

        if (accessToken) {
          oauthProcessingRef.current = true;
          setOauthLoading("google");
          setError("");
          // Process the OAuth token (same as onSuccess handler)
          (async () => {
            try {
              // Get user info from Google using httpRequest to leverage native HTTP on mobile
              const userInfoResponse = await httpRequest('https://www.googleapis.com/oauth2/v2/userinfo', {
                headers: { Authorization: `Bearer ${accessToken}` }
              });

              const userInfo = await userInfoResponse.json();

              if (!userInfo.email) {
                throw new Error('Google account email not found.');
              }

              // Send to backend
              const result = await authService.oauthCallback(
                'google',
                accessToken,
                userInfo.email,
                userInfo.name || userInfo.email,
                rememberMe
              );
              await onLogin(result.is_new_user || false);
              // Clear hash from URL
              window.history.replaceState(null, '', window.location.pathname);
              localStorage.removeItem('oauth_redirect_pending');
              await handlePostAuth();
            } catch (err) {
              setError(err.message || 'Google login failed. Please try again.');
              setOauthLoading(null);
              // Clear hash from URL
              window.history.replaceState(null, '', window.location.pathname);
              localStorage.removeItem('oauth_redirect_pending');
            }
          })();
        }
      } else if (url.includes(frontendHost) && hash) {
        // We're on the redirect page but no token - might be an error
        const params = new URLSearchParams(hash.substring(1));
        const error = params.get('error');
        if (error) {
          setError(`OAuth error: ${error}. ${params.get('error_description') || ''}`);
          setOauthLoading(null);
          window.history.replaceState(null, '', window.location.pathname);
          localStorage.removeItem('oauth_redirect_pending');
        }
      }
    };

    // Check immediately
    checkOAuthCallback();

    // Listen for hash changes (e.g., when app opens via deep link)
    const handleHashChange = () => {
      checkOAuthCallback();
    };
    window.addEventListener('hashchange', handleHashChange);

    // On mobile, also check periodically for OAuth callback
    let intervalId = null;
    if (isMobile) {
      // Check every 2 seconds for OAuth callback when on mobile
      intervalId = setInterval(() => {
        const hasPending = localStorage.getItem('oauth_redirect_pending') === 'true';
        if (hasPending) {
          checkOAuthCallback();
        }
      }, 2000);
    }

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [navigate, onLogin, isMobile, handlePostAuth, rememberMe, frontendHost]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let isNewUser = false;
      if (isRegister) {
        if (!legalAccepted) {
          setError("Please accept the legal documents to register.");
          setLoading(false);
          return;
        }
        const data = await authService.register(
          email,
          password,
          rememberMe,
          currentLegalVersion
        );
        isNewUser = true;
        analyticsService.track('user_registered', { provider: 'email' });

        if (data.requires_email_verification) {
          analyticsService.track('user_registered_email_sent', { email });
          setPendingEmail(email);
          setLoading(false);
          return;
        }

        if (pendingPersonaTitle) {
          try {
            await authService.updatePersona(pendingPersonaTitle, pendingPersonaIcon);
          } catch {
            // Non-fatal: persona save failure should not block login
          }
        }
      } else {
        const data = await authService.login(email, password, rememberMe);
        if (data.requires_email_verification) {
          setPendingEmail(email);
          setLoading(false);
          return;
        }
      }
      await onLogin(isNewUser);
      await handlePostAuth();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!pendingEmail) return { success: false, error: "No pending email address." };
    try {
      await authService.resendVerificationEmail(pendingEmail);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const handleChangeEmail = () => {
    setPendingEmail(null);
    setError("");
  };

  // Google OAuth handler - hook must always be called (React rules)
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const handleGoogleLogin = useGoogleLogin({
    flow: 'implicit', // Use implicit flow (token in URL fragment)
    onSuccess: async (tokenResponse) => {
      if (!googleClientId) {
        setError('Google OAuth is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID.');
        setOauthLoading(null);
        return;
      }
      setOauthLoading("google");
      setError("");
      try {
        // Get user info from Google
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
        });

        if (!userInfoResponse.ok) {
          throw new Error('Failed to fetch user info from Google');
        }

        const userInfo = await userInfoResponse.json();

        if (!userInfo.email) {
          throw new Error('Google account email not found. Please ensure your Google account has an email address.');
        }

        // Send to backend
        const result = await authService.oauthCallback(
          'google',
          tokenResponse.access_token,
          userInfo.email,
          userInfo.name || userInfo.email,
          rememberMe
        );
        await onLogin(result.is_new_user || false);
        await handlePostAuth();
      } catch (err) {
        setError(err.message || 'Google login failed. Please try again.');
      } finally {
        setOauthLoading(null);
      }
    },
    onError: (error) => {
      if (error?.error === 'popup_closed_by_user' || error?.error === 'popup_blocked') {
        // On mobile, popup is blocked - need to use redirect flow
        if (isMobile) {
          setError('Mobile OAuth requires redirect flow. Please configure redirect URI in Google Console.');
        } else {
          setError('Login cancelled. Please try again if you want to continue.');
        }
      } else if (error?.error === 'redirect_uri_mismatch') {
        setError(
          'Google OAuth redirect URI mismatch. ' +
          'Please add "http://localhost:3000" to both "Authorized JavaScript origins" and ' +
          '"Authorized redirect URIs" in Google Cloud Console > Credentials > OAuth 2.0 Client ID.'
        );
      } else {
        setError(`Google login failed: ${error?.error || error?.message || 'Unknown error'}. Please try again.`);
      }
      setOauthLoading(null);
    }
  });

  const handleOAuth = (provider) => {
    if (provider === "google") {
      if (googleClientId) {

        // On mobile, useGoogleLogin with popup doesn't work - need redirect flow with system browser
        if (isMobile) {
          // For mobile, use the web URL as redirect URI (Google requires HTTPS)
          // The web page will detect it's a mobile OAuth callback and redirect to custom URL scheme
          // IMPORTANT: This must match EXACTLY what's in Google Cloud Console (no trailing slash, no query params)
          const redirectUri = OAUTH_CONFIG.webRedirectUri;
          const scope = 'openid email profile';
          const responseType = 'token';
          const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(googleClientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=${responseType}&scope=${encodeURIComponent(scope)}`;

          setOauthLoading("google");
          setError("");

          // Store OAuth state to detect when we return
          localStorage.setItem('oauth_redirect_pending', 'true');

          // Open OAuth in system browser - after OAuth completes, Google redirects to the web URL
          // The web page will detect it's a mobile callback and redirect to pista://oauth-callback
          // which will open the app and we'll handle the callback in App.jsx
          Browser.open({ url: authUrl }).catch((_err) => {
            setError('Failed to open browser for OAuth. Please try again.');
            setOauthLoading(null);
          });
          return;
        }

        try {
          handleGoogleLogin();
        } catch (err) {
          setError('Failed to initiate Google login: ' + (err.message || String(err)));
        }
      } else {
        setError('Google OAuth is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID.');
      }
    }
  };

  if (pendingEmail) {
    return (
      <div className="login-container">
        <VerificationPending
          email={pendingEmail}
          onResend={handleResendVerification}
          onChangeEmail={handleChangeEmail}
        />
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <h2>{isRegister ? "Register" : "Login"}</h2>

        {/* OAuth Buttons */}
        <div className="oauth-buttons" style={{ marginBottom: "20px" }}>
          <button
            type="button"
            className="oauth-button google"
            onClick={() => {
              handleOAuth("google");
            }}
            disabled={loading || oauthLoading || !googleClientId}
            style={{
              width: "100%",
              padding: "10px",
              marginBottom: "10px",
              backgroundColor: googleClientId ? "#4285F4" : "#cccccc",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: googleClientId ? "pointer" : "not-allowed",
              fontSize: "14px",
              opacity: googleClientId ? 1 : 0.6
            }}
            title={!googleClientId ? "Google OAuth is not configured" : ""}
          >
            {oauthLoading === "google" ? "Loading..." : "Continue with Google"}
          </button>
        </div>

        <div style={{ textAlign: "center", margin: "20px 0", color: "#666" }}>
          <span>or</span>
        </div>

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.toLowerCase())}
              required
              autoFocus
              placeholder="your@email.com"
              data-testid="email-input"
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((show) => !show)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          {isRegister && (
            <div className="form-group auth-checkbox-row legal-consent-group">
              <label className="legal-consent-label" htmlFor="legalAccepted">
                <input
                  type="checkbox"
                  id="legalAccepted"
                  checked={legalAccepted}
                  onChange={(e) => setLegalAccepted(e.target.checked)}
                />
                <span>
                  I agree to the{" "}
                  <Link to="/legal" target="_blank" rel="noopener noreferrer">
                    Terms &amp; Conditions
                  </Link>,{" "}
                  <Link to="/legal#privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </Link>, and{" "}
                  <Link to="/legal#cookies" target="_blank" rel="noopener noreferrer">
                    Cookie Policy
                  </Link>
                  .
                </span>
              </label>
            </div>
          )}
          <div className="form-group auth-checkbox-row">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <label htmlFor="rememberMe">
              Remember me
            </label>
          </div>
          {error && <div className="error-message">{error}</div>}
          {!isRegister && (
            <div style={{ textAlign: "right", marginBottom: "0.5rem" }}>
              <Link to="/forgot-password" className="link-button" style={{ fontSize: "0.85rem" }}>
                Forgot password?
              </Link>
            </div>
          )}
          <button type="submit" disabled={loading || oauthLoading}>
            {loading ? "Loading..." : isRegister ? "Register" : "Login"}
          </button>
        </form>
        <p className="toggle-form">
          {isRegister ? "Already have an account? " : "Don't have an account? "}
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setIsRegister(!isRegister);
              setError("");
            }}
          >
            {isRegister ? "Login" : "Register"}
          </button>
        </p>

        {/* Get the App CTA - hide on native mobile since user already has the app */}
        {!isMobile && (
          <div style={{ marginTop: "24px", borderTop: "1px solid var(--border-color, #e5e7eb)", paddingTop: "24px" }}>
            <GetTheAppCTA />
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
